import React from 'react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import isEmail from 'validator/lib/isEmail';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import { Box, Flex, Text } from '@atlaskit/primitives';
import { default as AddIcon } from '@atlaskit/icon/glyph/editor/add';
import { Field, HelperMessage, ErrorMessage, ValidMessage } from '@atlaskit/form';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { ContactsTooltip, ContactsTooltipType } from '../..';
import './edit-create-invoice-group.scss';

export const validateEmailDomain = (email: string, validDomains: string[] | null | undefined) => {
  const domainFromEmail = email.split('@')[1];
  const matchedPartnerDomain = domainFromEmail && validDomains?.includes(domainFromEmail.toLowerCase());
  if (!matchedPartnerDomain) {
    return `Email address must have the same domain as the partner account or its associated accounts`;
  }
};
interface Props {
  recipients: (string | undefined | null)[];
  updateAdminFieldsDirtyFlag: (flag: boolean) => void;
  isRequired?: boolean;
  placeholder?: string;
  label?: string;
  title?: string;
  helperMessage?: string;
  namePrefix?: string;
  addButtonTitle?: string;
  validDomains?: string[] | null | undefined;
  version?: 2;
}

export const EditCreateInvoiceGroup = ({
  recipients,
  updateAdminFieldsDirtyFlag,
  isRequired,
  placeholder,
  label,
  title,
  helperMessage,
  namePrefix,
  addButtonTitle = 'Add invoice recipient',
  validDomains,
  version,
}: Props) => {
  const contactListOnFile = recipients.filter((recipient) => !isNil(recipient));
  const isVersionTwo = version === 2;
  // Extra '' so that textfield is shown initially if isVersionTwo
  const [contactList, setContactList] = React.useState(isVersionTwo ? [...contactListOnFile, ''] : contactListOnFile);
  const [nonEditableContacts, setNonEditableContacts] = React.useState<(string | null | undefined)[]>([]);

  const updateName = (event: React.FormEvent<HTMLInputElement>, index: number) => {
    const updatedAdminList = [...contactList];
    updatedAdminList[index] = event.currentTarget.value;
    setContactList(updatedAdminList);
    updateAdminFieldsDirtyFlag(true);
  };

  const removeContact = (index: number) => {
    const updatedAdminList = [...contactList];
    updatedAdminList.splice(index, 1);
    if (updatedAdminList.length === 0) {
      setContactList(['']);
      setNonEditableContacts([]);
      updateAdminFieldsDirtyFlag(true);
      return;
    }
    setContactList(updatedAdminList);
    setNonEditableContacts(updatedAdminList.slice(0, updatedAdminList.length - 1)); // we don't want to add currently adding value in textfield to nonEditableContacts
    updateAdminFieldsDirtyFlag(true);
  };

  const addContact = () => {
    setContactList((adminList) => [...adminList, '']);
    setNonEditableContacts(contactList);
    updateAdminFieldsDirtyFlag(true);
  };

  if (contactList && contactList.length === 0) {
    return null;
  }

  const renderTitle = () => {
    return (
      title && (
        <ContactsTooltip type={ContactsTooltipType.INVOICE}>
          <h3>{title}</h3>
        </ContactsTooltip>
      )
    );
  };

  const renderHelperMessage = () => {
    return helperMessage && <HelperMessage>{helperMessage}</HelperMessage>;
  };

  const renderAddButton = () => {
    return (
      <Button
        testId="add-button"
        onClick={addContact}
        iconBefore={<AddIcon label="add" />}
        appearance={isVersionTwo ? 'subtle' : 'link'}
      >
        {addButtonTitle}
      </Button>
    );
  };

  const renderContact = (admin: string, index: number) => {
    return (
      <Box paddingBlockStart="space.050" key={`${admin}-${index}`}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text color="color.text.subtle">{admin}</Text>
          <Button
            appearance="subtle"
            testId={`remove-button-${index}`}
            onClick={() => removeContact(index)}
            iconBefore={<TrashIcon label="remove" />}
          />
          <VisuallyHidden>
            <Field name={namePrefix ? `${namePrefix}${index}` : `recipients[${index}]`} defaultValue={admin}>
              {() => <Textfield testId="invoice-contact-hidden" value={admin} type="hidden" appearance="none" />}
            </Field>
          </VisuallyHidden>
        </Flex>
      </Box>
    );
  };

  const renderContactsListOnFile = (shouldShowBorder?: boolean) => {
    return contactListOnFile
      .filter((item) => contactList.includes(item))
      .map((admin, index) => {
        if (admin) {
          if (isVersionTwo) {
            return renderContact(admin, index);
          }

          return (
            <>
              <div
                key={index}
                className={classNames('billing-admins invoice-recipients', {
                  'billing-admins-v2': isVersionTwo,
                })}
              >
                <Field
                  name={namePrefix ? `${namePrefix}${index}` : `recipients[${index}]`}
                  defaultValue={admin}
                  isRequired={isRequired}
                >
                  {() => <div data-testid="invoice-contact">{admin}</div>}
                </Field>

                <Button
                  className="remove-row remove-billing-button"
                  testId="remove-button"
                  onClick={() => removeContact(index)}
                  appearance="link"
                >
                  Remove
                </Button>
              </div>

              {shouldShowBorder && <hr className="recipient-border" />}
            </>
          );
        }
      });
  };

  const renderContactsList = (shouldShowBorder?: boolean) => {
    return contactList.map((admin, index) => {
      if (contactListOnFile.includes(admin) && admin !== '') return null;

      if (isVersionTwo && admin && nonEditableContacts.includes(admin)) {
        return renderContact(admin, index);
      }

      return (
        <>
          <div
            key={index}
            className={classNames('billing-admins', {
              'contact-field-container': isVersionTwo,
            })}
          >
            <Field
              name={namePrefix ? `${namePrefix}${index}` : `recipients[${index}]`}
              defaultValue={admin}
              isRequired={isRequired}
              label={label ?? ''}
              validate={() => {
                if (admin) {
                  return isEmail(admin)
                    ? validDomains
                      ? validateEmailDomain(admin, validDomains)
                      : undefined
                    : `${admin} must be a valid e-mail address`;
                }
              }}
            >
              {({ fieldProps, error, valid }: any) => (
                <>
                  <Textfield
                    {...fieldProps}
                    testId="invoice-contact-add"
                    placeholder={placeholder ?? ''}
                    autoComplete="off"
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      updateName(event, index);
                      fieldProps.onChange(event);
                    }}
                    value={admin}
                  />
                  {isVersionTwo && fieldProps.value && valid && !error && (
                    <ValidMessage>Valid invoice contact</ValidMessage>
                  )}
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                </>
              )}
            </Field>

            {!isVersionTwo && (
              <Button
                className="remove-row remove-billing-button"
                testId="remove-button"
                onClick={() => removeContact(index)}
                appearance="link"
              >
                Remove
              </Button>
            )}
          </div>

          {shouldShowBorder && <hr className="recipient-border" />}
        </>
      );
    });
  };

  if (isVersionTwo) {
    const validContactListOnFile = renderContactsListOnFile().filter((item) => item);
    const isContactListOnFileEmpty = validContactListOnFile.length === 0;

    return (
      <>
        {renderTitle()}
        {renderHelperMessage()}
        {isContactListOnFileEmpty || (
          <div className="recipient-heading-v2">
            Recipient {validContactListOnFile.length === 1 ? 'email' : 'emails'}
          </div>
        )}
        {validContactListOnFile}
        {renderContactsList()}
        <div
          className={classNames('mt-8', {
            'add-button-v2': isVersionTwo,
          })}
        >
          {renderAddButton()}
        </div>
      </>
    );
  }

  return (
    <>
      {renderTitle()}
      <>
        {renderHelperMessage()}
        <div className="recipient-heading">Recipient</div>
        <hr className="recipient-border" />
      </>
      {renderContactsListOnFile(true)}
      {renderContactsList(true)}
      {renderAddButton()}
    </>
  );
};

export default EditCreateInvoiceGroup;
